/**
 * Decode HTML entities in API text that is rendered as plain text (not
 * dangerouslySetInnerHTML). The backend's rich-text fields (descriptions,
 * bios, etc.) sometimes come through with literal HTML entities baked in -
 * usually because the source text was pasted from Word/a webpage that HTML-
 * encodes special characters (£ -> &pound;, ' -> &rsquo;, etc.) - and since
 * these fields are shown as plain React text, the browser never gets a
 * chance to interpret those entities itself.
 *
 * This decodes ALL named and numeric HTML entities generically (via the
 * browser's own parser), instead of the ad-hoc `.replace(/&amp;/g, "&")...`
 * chains scattered around the app, which only ever cover a handful of
 * entities by hand and silently leave anything else (like &pound;) as
 * literal text.
 */
let decoderEl: HTMLTextAreaElement | null = null;

export const decodeHtmlEntities = (value?: string | null): string => {
  const input = value ?? "";
  if (!input) return "";

  // SSR / non-DOM environments (tests, server rendering): fall back to a
  // no-op rather than throwing.
  if (typeof document === "undefined") return input;

  if (!decoderEl) {
    decoderEl = document.createElement("textarea");
  }
  // Setting innerHTML on a <textarea> only decodes entities in its text
  // content - it never executes scripts or renders markup, so this is safe
  // even for untrusted input.
  decoderEl.innerHTML = input;
  return decoderEl.value;
};

/** Strip tags first (for fields that may also contain stray HTML), then
 *  decode entities and trim. Use for plain-text display of rich-text API
 *  fields (descriptions, bios, titles). */
export const sanitizeText = (value?: string | null): string =>
  decodeHtmlEntities((value ?? "").replace(/<[^>]*>/g, "")).trim();
